// global-navigation.js
// This file handles things related to the mobile navigation including hiding/showing it,
import cookies from 'js-cookie';
import { throttle } from 'throttle-debounce';
import { select } from './utils/dom';

// the mobile navigation header
const header = select('.SiteHeader--mobile');

// body offset initially 0
let bodyOffset = 0;

/**
 * Mobile navigation emergency contact link open/close
 */
class MobileEmergencyContact {
  constructor() {
    this.DOM = {
      el: select('.MobileNav__emergency'),
      link: select('.MobileNav__emergency-title'),
      content: select('.MobileNav__emergency-content'),
      close: select('.MobileNav__emergency-close'),
    };

    this.addEventListeners();
  }

  // add event listeners
  addEventListeners() {
    if (this.DOM.el) {
      this.DOM.link.addEventListener('click', this.show.bind(this));
      this.DOM.close.addEventListener('click', this.hide.bind(this));
    }
  }

  // show the emergency contact
  show() {
    this.DOM.content.style.display = 'block';
  }

  // hide the emergency contact
  hide() {
    this.DOM.content.style.display = 'none';
  }
}

/**
 * A logged in user can see the portal menu and this class handles the sliding open / closed
 * of that portal menu on mobile devices.
 */
class MobilePortalMenu {
  constructor() {
    if (!select('.MobileNav__portal')) {
      return;
    }

    // some DOM
    this.DOM = {
      link: select('.Login', select('.MobileNav')),
      menuContainer: select('.MobileNav'),
      menu: select('.MobileNav__portal'),
      backLink: select('.MobileNav__portal-back'),
      logout: select('.PortalLink__logout'),
    };

    // the class used to show / hide the portal navigation
    this.showClass = 'show-portal-nav';

    // the ID used to track the menu (inserted with JS to follow Fake Menu pattern)
    this.menuId = 'portal-navigation';

    this.addEventListeners();
    this.build();
  }

  // add event listeners
  addEventListeners() {
    if (this.DOM.link) {
      this.DOM.link.addEventListener('click', (event) => {
        if (select('.MobileNav .PortalLink').childElementCount > 0) {
          // could have been a click on "log out" link or link to dashboard in which case ignore
          if (event.target.tagName.toLowerCase() !== 'a' && event.target.parentNode.tagName.toLowerCase() !== 'a') {
            this.show();
          }
        }
      });
    }
    if (this.DOM.backLink) {
      this.DOM.backLink.addEventListener('click', this.hide.bind(this));
    }
  }

  // build accessibility stuff
  build() {
    if (this.DOM.link) {
      this.DOM.link.setAttribute('aria-role', 'button');
      this.DOM.link.setAttribute('aria-expanded', 'false');
    }
    this.DOM.menu.id = `${this.menuId}`;
    this.DOM.menu.setAttribute('aria-hidden', true);
  }

  // show the portal menu
  show() {
    if (this.DOM.link) {
      this.DOM.link.setAttribute('aria-controls', this.menuId);
      this.DOM.link.setAttribute('aria-expanded', 'true');
    }
    this.DOM.menu.setAttribute('aria-hidden', false);
    this.DOM.menuContainer.classList.add(this.showClass);
  }

  // hide the portal menu
  hide() {
    if (this.DOM.link) {
      this.DOM.link.setAttribute('aria-expanded', false);
      this.DOM.link.removeAttribute('aria-controls');
    }
    this.DOM.menu.setAttribute('aria-hidden', true);
    this.DOM.menuContainer.classList.remove(this.showClass);
  }
}

/**
 * Toggling the mobile nav open / closed.
 * Also handles the ui-scroll attribute on the body.
 */
class MobileNavigationExpand {
  constructor() {
    if (!header) {
      return;
    }

    this.DOM = {
      nav: select('.MobileNav'),
      toggle: select('.toggle-navigation'),
      googleTranslate: select('.SiteHeader--googleTranslate'),
      searchMobileTrigger: select('.SearchModalMobileTrigger'),
      searchModal: select('.SearchModal'),
    };

    this.mobilePortalMenu = new MobilePortalMenu();

    this.addEventListeners();
  }

  // add event listeners
  addEventListeners() {
    // Handle taps on the menu hide / show mobile navigation button button.
    if (this.DOM.toggle) {
      this.DOM.toggle.addEventListener('click', this.toggleOpen.bind(this));
    }

    // Adds a little drop shadow to the header when page is scrolled.
    // This is a part of this class becuase it also uses the toggleScroll function.
    window.addEventListener('scroll', throttle(100, this.toggleScroll.bind(this, null)));
  }

  /**
   * Toggles the state of the overflow property (through the ui-scroll attribute)
   * on the body. This is helpful for ensuring the page does not scroll behind the
   * open naviation.
   *
   * @param {Boolean}  scroll  Whether scroll should be enabled.
   */
  toggleScroll(scroll) { // eslint-disable-line class-methods-use-this
    if (scroll) {
      document.body.scrollTop = bodyOffset;
    } else {
      bodyOffset = document.body.scrollTop;
    }

    document.body.setAttribute('ui-scroll', scroll);
  }

  /**
   * Called when the menu needs to be "open" like on mobile
   */
  toggleOpen() {
    const navIsOpen = header.classList.contains('is-open');

    if (!navIsOpen) {
      // scroll to 0 to avoid trashing and the ios issue of scrolling behind
      this.toggleScroll(false);
      header.setAttribute('aria-expanded', true);
      header.classList.add('is-open');
      this.DOM.toggle.setAttribute('aria-expanded', true);
      // push the mobile menu down if there's a notification
      if (select('.notification-bar') || select('.app-notification-container')) {
        let notificationHeight = 0;
        if (select('.notification-bar')) {
          notificationHeight = select('.notification-bar').getBoundingClientRect().height;
        }
        let appNotificationHeight = 0;
        const appNotificationBar = select('.app-notification-container');
        if (appNotificationBar && appNotificationBar.style.visibility !== 'hidden') {
          appNotificationHeight = appNotificationBar.getBoundingClientRect().height;
        }
        let googleTranslateHeight = 0;

        if (this.DOM.googleTranslate) {
          // googleTranslateHeight = this.DOM.googleTranslate.getBoundingClientRect().height;
          googleTranslateHeight = 46;

          const cookieVal = cookies.getJSON('googtrans');

          if (cookieVal) {
            googleTranslateHeight += 40;
          }
        }

        this.DOM.nav.style.top = `${googleTranslateHeight + appNotificationHeight + notificationHeight + 100}px`;
      }
      // check if user is logged in/in the portal
      if (select('.MobileNav .PortalLink') && select('.MobileNav .PortalLink').childElementCount > 0 && this.mobilePortalMenu.DOM) {
        // open portal nav by default
        this.mobilePortalMenu.show();
      }
      if (this.DOM.searchMobileTrigger) {
        this.DOM.searchMobileTrigger.classList.add('is-visible');
      }
    } else {
      // reset the scroll position for the user
      this.toggleScroll(true);
      header.setAttribute('aria-expanded', false);
      header.classList.remove('is-open');
      this.DOM.toggle.focus();
      if (this.DOM.searchMobileTrigger) {
        this.DOM.searchMobileTrigger.classList.remove('is-visible');
      }
      if (this.DOM.searchModal) {
        this.DOM.searchModal.classList.remove('is-open');
      }
    }
  }
}

// initialize mobile menus.
new MobileNavigationExpand(); // eslint-disable-line no-new
new MobileEmergencyContact(); // eslint-disable-line no-new
new MobilePortalMenu(); // eslint-disable-line no-new

/**
 * On window scroll.
 */
const onScroll = () => {
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
  const currentScrollPosition = scrollTop >= 0 ? scrollTop : 0;
  document.body.setAttribute('ui-scrolled', !!currentScrollPosition);
};

// This adds a little drop shadow to the header when page is scrolled.
// CSS handles this based on the ui-scrolled attribute on the body.
window.addEventListener('scroll', throttle(100, onScroll));



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/shared/global-navigation.js